<template>
  <article
    class="service-pricing-card flex h-full flex-col"
    :class="[
      `service-pricing-card--${variant}`,
      { 'service-pricing-card--highlighted': highlighted },
    ]"
  >
    <div
      v-if="image"
      class="service-pricing-card__cover"
    >
      <img
        :src="image"
        :alt="title || ''"
        class="service-pricing-card__cover-img"
      >
    </div>

    <div class="service-pricing-card__head">
      <div
        v-if="!image"
        class="service-pricing-card__icon"
        :class="{ 'service-pricing-card__icon--default': !icon }"
        :aria-hidden="icon ? undefined : true"
      >
        <UIcon
          v-if="icon"
          :name="icon"
          class="size-7"
        />
        <template v-else>
          <span class="service-pricing-card__blob service-pricing-card__blob--lg" />
          <span class="service-pricing-card__blob service-pricing-card__blob--sm service-pricing-card__blob--a" />
          <span class="service-pricing-card__blob service-pricing-card__blob--sm service-pricing-card__blob--b" />
        </template>
      </div>

      <h3 class="service-pricing-card__title">
        {{ title }}
      </h3>

      <p
        v-if="price"
        class="service-pricing-card__price"
      >
        {{ price }}
      </p>

      <p class="service-pricing-card__desc">
        {{ description }}
      </p>

      <button
        v-if="ctaLabel"
        type="button"
        class="service-pricing-card__cta"
        @click.stop="handleCta"
      >
        <span>{{ ctaLabel }}</span>
        <UIcon
          :name="ctaIcon"
          class="size-4 shrink-0"
        />
      </button>
      <NuxtLink
        v-if="detailLink && !builderPreview"
        :to="detailLink"
        class="service-pricing-card__details"
      >
        مشاهده جزئیات خدمت
      </NuxtLink>
    </div>

    <div
      v-if="featureItems.length"
      class="service-pricing-card__body"
    >
      <ul class="service-pricing-card__features">
        <li
          v-for="(feature, i) in featureItems"
          :key="i"
          class="service-pricing-card__feature"
        >
          <span class="service-pricing-card__check" aria-hidden="true">
            <UIcon
              name="i-lucide-check"
              class="size-3"
            />
          </span>
          <span>{{ feature }}</span>
        </li>
      </ul>
    </div>
  </article>
</template>

<script setup lang="ts">
import type { ServiceCardVariant } from '~/constants/serviceCardVariants'

const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    price?: string
    image?: string
    icon?: string
    ctaLabel?: string
    ctaLink?: string
    detailLink?: string
    ctaIcon?: string
    features?: string[]
    variant?: ServiceCardVariant
    highlighted?: boolean
    builderPreview?: boolean
    /** Preferred preview handler (avoids Vue on* listener collision). */
    previewNavigate?: (to: string) => void
    /** @deprecated Prefer previewNavigate */
    onNavigate?: (to: string) => void
    /** When set, CTA opens this handler instead of navigating. */
    ctaHandler?: () => void
  }>(),
  {
    features: () => [],
    ctaIcon: 'i-lucide-phone',
    variant: 'solid',
    highlighted: false,
  },
)

const emit = defineEmits<{
  cta: []
}>()

const route = useRoute()

const featureItems = computed(() =>
  (props.features ?? []).filter((f) => Boolean(f && String(f).trim())),
)

const BOOKING_WIZARD_ID = 'booking-wizard'

function normalizePath(path: string): string {
  const cleaned = path.replace(/\/$/, '') || '/'
  return cleaned
}

function scrollToAnchor(id: string): boolean {
  if (!import.meta.client || !id) return false
  const el = document.getElementById(id)
  if (!el) return false
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  return true
}

function runPreviewNavigate(to: string): boolean {
  const fn = props.previewNavigate ?? props.onNavigate
  if (!fn) return false
  // Builder always passes a handler; use it even if builderPreview was omitted.
  if (props.builderPreview || props.previewNavigate) {
    fn(to)
    return true
  }
  return false
}

function handleCta() {
  if (props.ctaHandler) {
    props.ctaHandler()
    return
  }
  const instance = getCurrentInstance()
  if (instance?.vnode.props?.onCta) {
    emit('cta')
    return
  }
  const to = props.ctaLink?.trim()
  if (!to) return
  if (runPreviewNavigate(to)) return
  if (!import.meta.client) return

  if (to.startsWith('http') || to.startsWith('tel:') || to.startsWith('mailto:')) {
    window.open(to, to.startsWith('http') ? '_blank' : '_self')
    return
  }

  if (to.startsWith('#')) {
    scrollToAnchor(to.slice(1))
    return
  }

  const hashIndex = to.indexOf('#')
  const pathPart = hashIndex >= 0 ? to.slice(0, hashIndex) : to
  const hashPart = hashIndex >= 0 ? to.slice(hashIndex + 1) : ''
  const queryIndex = pathPart.indexOf('?')
  const search = queryIndex >= 0 ? pathPart.slice(queryIndex) : ''
  const targetPath = normalizePath((queryIndex >= 0 ? pathPart.slice(0, queryIndex) : pathPart) || '/')
  const currentPath = normalizePath(route.path)
  const fullTarget = `${targetPath}${search}${hashPart ? `#${hashPart}` : ''}`

  // Same page (e.g. CTA on /booking that points to /booking): keep the query
  // (service preselect) and scroll to the wizard instead of a no-op.
  if (currentPath === targetPath) {
    if (search) void navigateTo(fullTarget)
    if (hashPart && scrollToAnchor(hashPart)) return
    scrollToAnchor(BOOKING_WIZARD_ID)
    return
  }

  void navigateTo(fullTarget)
}
</script>

<style scoped>
.service-pricing-card {
  --spc-theme: var(--ui-primary, #2563eb);
  --spc-radius: 1.25rem;
  --spc-bg: #ffffff;
  --spc-border: #e2e8f0;
  --spc-text: #0f172a;
  --spc-muted: #64748b;
  --spc-line: #f1f5f9;
  --spc-check-bg: color-mix(in srgb, var(--spc-theme) 10%, white);
  --spc-check-border: transparent;
  --spc-check-color: var(--spc-theme);
  --spc-cta-bg: var(--spc-theme);
  --spc-cta-border: var(--spc-theme);
  --spc-cta-text: #ffffff;
  --spc-cta-hover-bg: color-mix(in srgb, var(--spc-theme) 86%, black);
  --spc-cta-hover-text: #ffffff;
  --spc-cta-hover-border: color-mix(in srgb, var(--spc-theme) 86%, black);

  container-type: inline-size;
  background: var(--spc-bg);
  color: var(--spc-text);
  border: 1px solid var(--spc-border);
  border-radius: var(--spc-radius);
  overflow: hidden;
  box-shadow: 0 3px 12px rgb(15 23 42 / 5%);
  transition:
    border-color 0.2s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background-color 0.2s ease;
}

.service-pricing-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px color-mix(in srgb, var(--spc-theme) 14%, transparent);
}

/* —— solid (default) —— */
.service-pricing-card--solid:hover {
  border-color: color-mix(in srgb, var(--spc-theme) 38%, #e2e8f0);
}

.service-pricing-card--solid.service-pricing-card--highlighted {
  border-color: color-mix(in srgb, var(--spc-theme) 55%, white);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--spc-theme) 12%, transparent), 0 8px 24px color-mix(in srgb, var(--spc-theme) 12%, transparent);
}

/* —— outline —— */
.service-pricing-card--outline {
  --spc-border: color-mix(in srgb, var(--spc-theme) 35%, #e2e8f0);
  --spc-cta-bg: transparent;
  --spc-cta-border: color-mix(in srgb, var(--spc-theme) 38%, transparent);
  --spc-cta-text: var(--spc-theme);
  --spc-cta-hover-bg: var(--spc-theme);
  --spc-cta-hover-text: #ffffff;
  --spc-cta-hover-border: var(--spc-theme);
}

.service-pricing-card--outline:hover {
  border-color: var(--spc-theme);
}

/* Full :global(...) — Vue scoped strips the descendant if only `.dark` is global.
   Extra class bumps specificity over scoped light rules (`class` + `[data-v]`). */
:global(.dark .service-pricing-card) {
  --spc-bg: #111827;
  --spc-border: #273449;
  --spc-text: #f8fafc;
  --spc-muted: #aab6c8;
  --spc-line: #263244;
  --spc-check-bg: color-mix(in srgb, var(--spc-theme) 18%, #111827);
  box-shadow: 0 3px 12px rgb(0 0 0 / 12%);
}

:global(.dark .service-pricing-card.service-pricing-card--outline) {
  --spc-border: color-mix(in srgb, var(--spc-theme) 55%, #273449);
  --spc-cta-bg: transparent;
  --spc-cta-border: color-mix(in srgb, var(--spc-theme) 60%, transparent);
  --spc-cta-text: color-mix(in srgb, white 85%, var(--spc-theme));
}

/* —— soft —— */
.service-pricing-card--soft {
  --spc-bg: color-mix(in srgb, var(--spc-theme) 3%, white);
  --spc-border: color-mix(in srgb, var(--spc-theme) 16%, #e2e8f0);
  --spc-cta-bg: color-mix(in srgb, var(--spc-theme) 10%, white);
  --spc-cta-border: transparent;
  --spc-cta-text: var(--spc-theme);
  --spc-cta-hover-bg: var(--spc-theme);
  --spc-cta-hover-text: #ffffff;
  --spc-cta-hover-border: var(--spc-theme);
}

.service-pricing-card--soft:hover {
  border-color: color-mix(in srgb, var(--spc-theme) 35%, transparent);
}

:global(.dark .service-pricing-card.service-pricing-card--soft) {
  --spc-bg: color-mix(in srgb, var(--spc-theme) 7%, #111827);
  --spc-border: color-mix(in srgb, var(--spc-theme) 26%, #273449);
  --spc-cta-bg: color-mix(in srgb, var(--spc-theme) 20%, #111827);
  --spc-cta-text: color-mix(in srgb, white 88%, var(--spc-theme));
}

/* —— ghost —— */
.service-pricing-card--ghost {
  --spc-bg: transparent;
  --spc-border: transparent;
  --spc-cta-bg: transparent;
  --spc-cta-border: color-mix(in srgb, var(--spc-theme) 38%, transparent);
  --spc-cta-text: var(--spc-theme);
  --spc-cta-hover-bg: color-mix(in srgb, var(--spc-theme) 8%, transparent);
  --spc-cta-hover-text: var(--spc-theme);
  --spc-cta-hover-border: var(--spc-theme);
}

.service-pricing-card--ghost:hover {
  box-shadow: none;
  transform: none;
}

.service-pricing-card__cover {
  width: 100%;
  aspect-ratio: 16 / 8;
  max-height: 12rem;
  overflow: hidden;
  position: relative;
  background: color-mix(in srgb, var(--spc-theme) 12%, #e2e8f0);
}

.service-pricing-card__cover-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 42%;
  display: block;
  transition: transform 0.35s ease;
}

.service-pricing-card:hover .service-pricing-card__cover-img {
  transform: scale(1.035);
}

.service-pricing-card__head {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: stretch;
  gap: 0.55rem;
  padding: 1rem 1.1rem 1.1rem;
  text-align: start;
}

.service-pricing-card__icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.85rem;
  background: color-mix(in srgb, var(--spc-theme) 10%, transparent);
  color: var(--spc-theme);
  margin-inline-end: auto;
}

.service-pricing-card__icon--default {
  width: 2.5rem;
  height: 2.5rem;
}

.service-pricing-card__blob {
  position: absolute;
  border-radius: 9999px;
  background: currentColor;
}

.service-pricing-card__blob--lg {
  width: 1.35rem;
  height: 1.35rem;
  inset-inline-start: 0.15rem;
  top: 0.45rem;
}

.service-pricing-card__blob--sm {
  width: 0.7rem;
  height: 0.7rem;
}

.service-pricing-card__blob--a {
  inset-inline-start: 1.45rem;
  top: 0.2rem;
}

.service-pricing-card__blob--b {
  inset-inline-start: 1.55rem;
  top: 1.15rem;
}

.service-pricing-card__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5;
  color: var(--spc-text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.service-pricing-card__price {
  align-self: flex-start;
  margin: 0;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--spc-theme) 9%, transparent);
  color: var(--spc-theme);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.5;
}

.service-pricing-card__desc {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--spc-muted);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.service-pricing-card__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  margin-top: auto;
  padding: 0.6rem 0.8rem;
  border-radius: 0.8rem;
  border: 1px solid var(--spc-cta-border);
  background: var(--spc-cta-bg);
  color: var(--spc-cta-text);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
}

.service-pricing-card__cta:hover {
  background: var(--spc-cta-hover-bg);
  color: var(--spc-cta-hover-text);
  border-color: var(--spc-cta-hover-border);
  box-shadow: 0 8px 20px color-mix(in srgb, black 10%, transparent);
}

.service-pricing-card__details {
  align-self: center;
  color: var(--spc-theme);
  font-size: 0.875rem;
  line-height: 1.5;
  text-decoration: none;
}

.service-pricing-card__details:hover {
  color: var(--spc-text);
}

.service-pricing-card--ghost .service-pricing-card__cta:hover {
  box-shadow: none;
}

.service-pricing-card__body {
  border-top: 1px solid var(--spc-line);
  padding: 0.85rem 1.1rem 1rem;
  margin-top: auto;
}

.service-pricing-card__features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.service-pricing-card__feature {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.82rem;
  line-height: 1.55;
  color: var(--spc-muted);
}

.service-pricing-card__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
  border-radius: 9999px;
  background: var(--spc-check-bg);
  color: var(--spc-check-color);
  border: 1px solid var(--spc-check-border);
}

@container (max-width: 220px) {
  .service-pricing-card__head {
    gap: 0.4rem;
    padding: 0.7rem;
  }

  .service-pricing-card__icon,
  .service-pricing-card__icon--default {
    width: 2rem;
    height: 2rem;
  }

  .service-pricing-card__title {
    font-size: 0.88rem;
  }

  .service-pricing-card__desc {
    font-size: 0.76rem;
    line-height: 1.5;
    -webkit-line-clamp: 2;
  }

  .service-pricing-card__cta {
    gap: 0.25rem;
    padding: 0.48rem 0.45rem;
    font-size: 0.72rem;
  }

  .service-pricing-card__details {
    font-size: 0.72rem;
  }

  .service-pricing-card__body {
    padding: 0.65rem 0.7rem 0.8rem;
  }

  .service-pricing-card__features {
    gap: 0.4rem;
  }

  .service-pricing-card__feature {
    gap: 0.4rem;
    font-size: 0.74rem;
  }

  .service-pricing-card__check {
    width: 1rem;
    height: 1rem;
  }
}
</style>
